<template>
    <div class="container">
        <span class="container-title">
            Nova Integração ERP
            <span v-if="form.data.type" class="new-integration">
                <span class="hiffen">-</span>
                {{ typeLabelComputed }}
            </span>
        </span>

        <div v-if="loading.general" class="container-loader">
            <ui-load-indicator bg-color="#e7e9f4" color="#69D296" :h="50" :stroke="8" />
        </div>

        <div v-else>
            <div v-if="!showOtherConfiguration" class="container-add">
                <div class="button-exit">
                    <ui-button design="sm line" @click="returnIntegrationErp">
                        <em class="far fa-chevron-left" />
                    </ui-button>
                </div>

                <div class="data-integration">
                    <span class="data-integration-title">Dados para Integração</span>
                    <span class="data-integration-subtitle">
                        Informe os dados da integração do erp ao qual você contratou.
                    </span>
                </div>

                <div class="hr" />

                <div class="body-integration">
                    <div class="body-integration-select">
                        <div class="body-integration-select-type-company mgb-15">
                            <ui-input
                                v-model="form.data.business_name"
                                :required="form.fields.business_name.required"
                                :disabled="form.fields.business_name.disabled"
                                label="Nome da Empresa:"
                                type="text"
                            />

                            <div v-if="form.data.type === 'uau'" class="body-integration-select-type-company-id">
                                <ui-input
                                    v-model="form.data.business_id"
                                    :required="form.fields.business_id.required"
                                    :disabled="form.fields.business_id.disabled"
                                    label="Código da Empresa:"
                                    type="text"
                                />
                            </div>
                        </div>

                        <ui-select-v2
                            v-model="form.data.type"
                            :data="form.typeOptions"
                            :required="form.fields.type.required"
                            :disabled="form.fields.type.disabled"
                            label="Tipo de Integração:"
                            return-value="value"
                            @input="typeHandler"
                        />
                    </div>

                    <div class="body-integration-url">
                        <ui-input
                            v-model="form.data.base_url"
                            :required="form.fields.base_url.required"
                            :disabled="form.fields.base_url.disabled"
                            :invertgroup="true"
                            label="URL de Integração:"
                            groupicon="fal fa-link"
                            type="text"
                        />
                    </div>

                    <div class="body-integration-user">
                        <div class="user">
                            <ui-input
                                v-model="form.data.user"
                                :required="form.fields.user.required"
                                :disabled="form.fields.user.disabled"
                                :invertgroup="true"
                                label="Usuário:"
                                groupicon="fal fa-user"
                                type="text"
                            />
                        </div>

                        <div class="password">
                            <ui-input
                                v-model="form.data.pass"
                                :required="form.fields.pass.required"
                                :disabled="form.fields.pass.disabled"
                                :invertgroup="true"
                                label="Senha:"
                                groupicon="fal fa-lock"
                                type="password"
                            />
                        </div>
                    </div>

                    <div v-if="form.data.type === 'uau'" class="body-integration-active-directory">
                        <div class="body-integration-active-directory-mode-corporate">
                            <ui-switch
                                v-model="form.data.data.mode_corporate"
                                :checked="form.data.data.mode_corporate"
                                label="Active Directory"
                                design="xs primary"
                                @input="switchHandler" />
                        </div>

                        <div v-if="form.data.data.mode_corporate" class="body-integration-active-directory-user">
                            <ui-input
                                v-model="form.data.data.user_ad"
                                :required="form.fields.user_ad.required"
                                :disabled="form.fields.user_ad.disabled"
                                :invertgroup="true"
                                label="Usuário Active Direct:"
                            />
                        </div>
                    </div>
                </div>

                <div class="footer-container">
                    <div class="footer-container-oval-first" />
                    <div class="footer-container-oval-second" />

                    <div
                        class="footer-container-oval-icon"
                        :class="[{'footer-container-oval-icon-success': connectedInfo == true}]">
                        <em class="far fa-dot-circle" />
                    </div>

                    <span v-show="connectedInfo" class="footer-container-connected">Conectado com Sucesso.</span>
                    <span v-show="!connectedInfo" class="footer-container-not-connected">Não conectado.</span>

                    <div class="action-buttons">
                        <ui-button
                            v-if="!connectedInfo"
                            :disabled="!canSubmit"
                            :loading="loading.connect"
                            design="primary lg mw-150"
                            @click="checkConnection">
                            Testar Conexão
                        </ui-button>

                        <ui-button
                            v-else
                            :disabled="!canSubmit"
                            design="success lg mw-100"
                            @click="next">
                            Avançar
                            <em class="far fa-chevron-right"/>
                        </ui-button>
                    </div>
                </div>
            </div>

            <other-settings v-else :id="id" :form="form" @back="backHandler" />

            <div class="footer-add">
                <router-link to="/marketing/integracoes/erp/integracoes">
                    <span class="footer-add-integration-enterprises">Listar Integrações ERP</span>
                </router-link>

                <div class="footer-add-line-vertical" />

                <router-link to="/marketing/integracoes/erp/empreendimentos">
                    <span class="footer-add-integration-enterprises">Empreendimentos integrados</span>
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
	import { uiLoadIndicator } from '@/components/ui'
	import { uiButton } from '@/components/ui/buttons'
	import { uiInput, uiSwitch, uiSelectV2 } from '@/components/ui/form'
	import OtherSettings from '../other-settings'

	import { s3Filesystem, erp} from '@/services/axios'

	import { storage } from '@/services/storage'

	import { formMixin } from '@/mixins'

	const defaultFormData = () => ({
		version: 'v2',
		type: '',
		business_name: null,
		business_id: null,
		base_url: null,
		user: null,
		pass: null,
		show_value: '',
		color_available: '#1CCA85',
		color_unavailable: '#FF567B',
		hide_unusual_status: '',
		factor_x: 15,
		factor_y: 15,
		data: {
			uauProposalTime: '',
			uauClienteOriginCode: '',
			uauPercentContract: '',
			uauFutureValue: false,
			mode_corporate: false,
			user_ad: '',
			especieContrato: '',
			aplicacaoReajuste: '',
			taxaJurosMora: '',
			taxaMultaMora: ''
		}
	})

	const defaultLoading = () => ({
		general: false,
		connect: false
	})

	export default {
		name: 'add-integration',

		components: {
			uiButton,
			uiSelectV2,
			uiSwitch,
			uiInput,
			OtherSettings,
			uiLoadIndicator
		},

		mixins: [formMixin],

		props: {
			id: {
				type: [Number,String],
				required: false,
				default: 0,
			},
		},

		data(){
			return {
				form: {
					data: { ...defaultFormData() },
					fields: {
						type: {
							required: true,
							disabled: false
						},
						business_name: {
							required: true,
							disabled: false
						},
						business_id: {
							required: true,
							disabled: false
						},
						base_url: {
							required: true,
							disabled: false
						},
						user: {
							required: true,
							disabled: false
						},
						pass: {
							required: true,
							disabled: false
						},
						show_value: {
							required: true,
							disabled: false
						},
						color_available: {
							required: true,
							disabled: false
						},
						color_unavailable: {
							required: true,
							disabled: false
						},
						hide_unusual_status: {
							required: true,
							disabled: false
						},
						factor_x: {
							required: false,
							disabled: false
						},
						factor_y: {
							required: false,
							disabled: false
						},
						uauProposalTime: {
							required: false,
							disabled: false
						},
						uauClientOriginCode: {
							required: false,
							disabled: false
						},
						uauPercentContract: {
							required: false,
							disabled: false
						},
						uauFutureValue: {
							required: false,
							disabled: false
						},
						user_ad: {
							required: false,
							disabled: true
						},
						especieContrato: {
							required: false,
							disabled: false
						},
						aplicacaoReajuste: {
							required: false,
							disabled: false
						},
						taxaJurosMora: {
							required: false,
							disabled: false
						},
						taxaMultaMora: {
							required: false,
							disabled: false
						}
					},
					typeOptions: [],
				},
				connectedInfo: false,
				loading: { ...defaultLoading() },
				showOtherConfiguration: false,
				allowedApps: null
			}
		},

		computed: {
			/**
			 * Computed property que retorna o valor do tipo de integração selecionado
			 */
			typeLabelComputed() {
				const type = this.form.typeOptions.find(item => this.form.data.type === item.value)
				return type ? type.label : ''
			},
			/**
			 * Computed property que valida o formulário e define se este pode ser
			 * submetido ou não
			 */
			canSubmit() {
				return this.validateForm().valid
			}
		},

		created() {
			this.$mx_copyOldForm()
			this.allowedApps = storage.get('allowed_apps')
		},

		mounted() {
			this.loadIntegrationTypes()
			this.loadData()
		},


		methods: {
			/**
			 * Handler que trata o evento de quando o switch é utilizado
			 */
			switchHandler(value) {
				this.form.fields.user_ad.required = value
				this.form.fields.user_ad.disabled = !value
			},
			/**
			 * Handler que trata o evento de quando o tipo de integração é selecionado
			 */
			typeHandler() {
				const isUAU = !!(this.form.data.type === 'uau')
				this.form.fields.business_id.required = isUAU
				this.form.fields.business_id.disabled = !isUAU
			},
			/**
			 * Valida a propriedade required dos campos do formulário
			 */
			validateRequired(field) {
				if (!Object.prototype.hasOwnProperty.call(this.form.fields, field)) {
					throw new Error(`Falha ao validar o campo ${field}. Este campo não existe!`)
				}
				const required = this.form.fields[field].required
				const validation = this.form.data[field] || this.form.data.data[field]

				return (required && validation) || !required
			},
			/**
			 * Valida os campos do formulário de acordo com diferentes conjuntos de regras
			 */
			validateForm() {
				let valid = true
				let message = '<ul>'
				try {
					if (!this.validateRequired('type')) {
						valid = false
						message += '<li>É necessário selecionar o tipo de integração.</li>'
					}

					if (!this.validateRequired('business_name')) {
						valid = false
						message += '<li>É necessário informar o nome da empresa para integração.</li>'
					}

					if (!this.validateRequired('business_id') && this.form.type ==! 'informakon') {
						valid = false
						message += '<li>É necessário informar o código da empresa para integração.</li>'
					}

					if (!this.validateRequired('base_url')) {
						valid = false
						message += '<li>É necessário informar a URL da integração.</li>'
					}

					if (!this.validateRequired('user')) {
						valid = false
						message += '<li>É necessário informar um nome de usuário para conexão.</li>'
					}

					if (!this.validateRequired('pass')) {
						valid = false
						message += '<li>É necessário informar a senha do usuário para conexão.</li>'
					}

					if (!this.validateRequired('user_ad')) {
						valid = false
						message += '<li>É necessário informar o usuário Active Direct para conexão.</li>'
					}

				} catch (e) {
					this.$toastr.e(e)
					valid = false
				}

				message += '</ul>'

				return { valid, message }
			},
			/**
			 * Busca a partir da S3 os tipos de Integração possíveis de serem cadastradas
			 */
			loadIntegrationTypes() {
				s3Filesystem.getIntegrationTypes()
					.then(response => {
						this.form.typeOptions = Object.values(response.data.type)
							.filter(option =>{
								if (this.checkAppsAllowed('uau') && option.value === 'uau') {
									return true
								}
								if (this.checkAppsAllowed('informakon') && option.value === 'informakon') {
									return true
								}
								return false
                			})
					})
					.catch(error => {
						if (error.data && error.data.msg) this.$toastr.e(error.data.msg)
						console.log(error)
					})
			},
			/**
			 * Carrega os dados de uma integração do banco de dados de acordo com o ID passado
			 * na rota
			 */
			loadData() {
				if (!this.id) return

				this.loading.general = true
				erp.getV2('', this.id)
					.then(response => {
						this.form.data = { ...response.data.data }
						/**
						 * A propriedade data é um JSON, por isso todas as propriedades do objeto se
						 * tornam String
						 */
						this.form.data.data.mode_corporate = this.form.data.data.mode_corporate.trim() &&
							this.form.data.data.mode_corporate.trim() === 'true'
						this.form.data.version = 'v2'
					})
					.catch(error => {
						if (error.data && error.data.msg) this.$toastr.e(error.data.msg)
						console.log(error)
					})
					.finally(() => {
						this.loading.general = false
					})
			},
			/**
			 * Testa a conexão com as configurações definidas para a integração
			 */
			checkConnection() {
				const params = (({
					type,
					business_name,
					business_id,
					base_url,
					user,
					pass,
					data
				}) => ({
					type,
					business_name,
					business_id,
					base_url,
					user,
					pass,
					mode_corporate: data.mode_corporate,
					user_ad: data.mode_corporate ? data.user_ad : ''
				}))(this.form.data)
				this.loading.connect = true

				erp.createV2('check', params)
					.then(response => {
						if (response.data.data) {
							this.connectedInfo = response.data.data.connected

						} else {
							this.connectedInfo = response.data.data
						}

						if (!this.connectedInfo) this.$toastr.e('Falha na conexão!')
					})
					.catch(error => {
						if (error.response.data && error.response.data.msg) {
							this.$toastr.e(error.response.data.msg)
						}
						console.log(error)
					})
					.finally(() => {
						this.loading.connect = false
					})
			},
			/**
			 * Passa para a próxima etapa do cadastro da integração
			 */
			next() {
				this.showOtherConfiguration = true
			},
			/**
			 * Função que permite retornar a lista de integrações
			 */
			returnIntegrationErp() {
				this.$router.push(`/marketing/integracoes/erp/integracoes`)
			},
			/**
			 * Handler function que trata o evento de quando se deseja voltar um passo
			 * no cadastro da integração
			 */
			backHandler(){
				this.connectedInfo = false
				this.showOtherConfiguration = false
			},
			/**
			 *Verifica quais são os apps disponíveis no allowedApps
			 */
			 checkAppsAllowed(name){
                return this.allowedApps.includes(name)
            },
		},
	}
</script>

<style lang="stylus" scoped>
@import 'styles.styl'
</style>